import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { t, getLocale } from '@/i18n';
import { PageLayout } from '@/components/layout/PageLayout';
import { PlanningCell } from '@/components/planning/PlanningCell';
import { GanttView } from '@/components/planning/GanttView';
import { ProductionOrderDialog } from '@/components/planning/ProductionOrderDialog';
import { AddPlanningDialog } from '@/components/planning/AddPlanningDialog';
import { OteChart } from '@/components/planning/OteChart';
import { SavingsCounter } from '@/components/planning/SavingsCounter';
import { EnergyPlanAdvisor } from '@/components/planning/EnergyPlanAdvisor';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ChevronLeft, ChevronRight, Info, ArrowDownToLine, ArrowUpFromLine, Package, Zap, Factory, CalendarDays } from 'lucide-react';
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from '@/components/ui/accordion';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import { cn } from '@/lib/utils';
import { useSimulation } from '@/hooks/useSimulation';

const OTE_OVERLAY_KEY = 'omya_ote_overlay';

const weekDays = [t('day.mon'), t('day.tue'), t('day.wed'), t('day.thu'), t('day.fri'), t('day.sat'), t('day.sun')];

// ISO week number helper
function getISOWeek(date) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
}

// Get Monday of the current week
function getMonday(date) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  d.setDate(diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

export default function Planovani() {
  const navigate = useNavigate();
  const { planningData, materialColors, orders } = useSimulation();
  const currentDayIdx = (new Date().getDay() + 6) % 7; // 0 = Mon

  const [weekOffset, setWeekOffset] = useState(0);
  const [dialogSlot, setDialogSlot] = useState(null);
  const [dialogLine, setDialogLine] = useState('');
  const [dialogLineId, setDialogLineId] = useState('');
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addDialogLine, setAddDialogLine] = useState(null);
  const [addDialogDayIdx, setAddDialogDayIdx] = useState(null);
  const [slotOverrides, setSlotOverrides] = useState({});
  const [oteOverlay, setOteOverlay] = useState(() => {
    if (typeof window === 'undefined') return true;
    const stored = localStorage.getItem(OTE_OVERLAY_KEY);
    return stored === null ? true : stored === '1';
  });

  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(OTE_OVERLAY_KEY, oteOverlay ? '1' : '0');
    }
  }, [oteOverlay]);

  // Compute displayed week's Monday and dates
  const weekMonday = useMemo(() => {
    const mon = getMonday(new Date());
    mon.setDate(mon.getDate() + weekOffset * 7);
    return mon;
  }, [weekOffset]);

  const weekNumber = useMemo(() => getISOWeek(weekMonday), [weekMonday]);

  const weekDates = useMemo(() =>
    weekDays.map((_, i) => {
      const d = new Date(weekMonday);
      d.setDate(d.getDate() + i);
      return d.getDate();
    }),
  [weekMonday]);

  // Merged planning data with overrides (for newly added slots)
  const mergedPlanningData = useMemo(() => {
    return {
      ...planningData,
      lines: planningData.lines.map(line => ({
        ...line,
        schedule: line.schedule.map((slot, idx) => {
          const key = `${line.id}-${idx}`;
          return slotOverrides[key] ?? slot;
        }),
      })),
    };
  }, [planningData, slotOverrides]);

  // Compute utilization breakdown
  const allSlots = mergedPlanningData.lines.flatMap(l => l.schedule);
  const occupied = allSlots.filter(s => s.material).length;
  const empty = allSlots.filter(s => !s.material).length;
  const total = allSlots.length;

  const utilizationData = [
    { name: t('planning.scheduled'), value: Math.round((occupied / total) * 100), color: 'hsl(var(--primary))' },
    { name: t('planning.available'), value: Math.round((empty / total) * 100), color: 'hsl(var(--status-success))' },
  ];

  // Weekly summary
  const totalPlanned = allSlots.reduce((s, sl) => s + (sl.plannedOutput || 0), 0);
  const totalActual = allSlots.reduce((s, sl) => s + (sl.actualOutput || 0), 0);
  const avgUtilization = Math.round(allSlots.filter(s => s.material).reduce((s, sl) => s + sl.utilization, 0) / Math.max(occupied, 1));

  return (
    <PageLayout title={t('planning.title')} concept>
      <div className="space-y-6">
        {/* How it works */}
        <Accordion type="single" collapsible className="bg-card rounded-xl border border-border shadow-sm">
          <AccordionItem value="how" className="border-none">
            <AccordionTrigger className="px-5 py-4 hover:no-underline">
              <div className="flex items-center gap-2.5">
                <Info className="h-4 w-4 text-primary" />
                <span className="font-semibold text-sm">{t('plan.howItWorks')}</span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-5 pb-5">
              <p className="text-sm text-muted-foreground mb-4">
                {t('plan.howItWorksDesc')}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Inputs */}
                <div className="bg-blue-50/80 rounded-xl p-4 space-y-3">
                  <div className="flex items-center gap-2 font-semibold text-sm text-blue-800">
                    <ArrowDownToLine className="h-4 w-4" />
                    {t('plan.input')}
                  </div>
                  <div className="space-y-2 text-sm text-blue-700">
                    <div className="flex items-center gap-2">
                      <Package className="h-3.5 w-3.5 shrink-0" />
                      <span>{t('plan.inputOrders')}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Factory className="h-3.5 w-3.5 shrink-0" />
                      <span>{t('plan.inputCapacities')}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CalendarDays className="h-3.5 w-3.5 shrink-0" />
                      <span>{t('plan.inputAvailability')}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Zap className="h-3.5 w-3.5 shrink-0" />
                      <span>{t('plan.inputTariffs')}</span>
                    </div>
                  </div>
                </div>
                {/* Outputs */}
                <div className="bg-green-50/80 rounded-xl p-4 space-y-3">
                  <div className="flex items-center gap-2 font-semibold text-sm text-green-800">
                    <ArrowUpFromLine className="h-4 w-4" />
                    {t('plan.output')}
                  </div>
                  <div className="space-y-2 text-sm text-green-700">
                    <div className="flex items-center gap-2">
                      <CalendarDays className="h-3.5 w-3.5 shrink-0" />
                      <span>{t('plan.outputWeeklyPlan')}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Factory className="h-3.5 w-3.5 shrink-0" />
                      <span>{t('plan.outputAssignment')}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Zap className="h-3.5 w-3.5 shrink-0" />
                      <span>{t('plan.outputTariffOpt')}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Package className="h-3.5 w-3.5 shrink-0" />
                      <span>{t('plan.outputTonnes')}</span>
                    </div>
                  </div>
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>

        {/* Week selector */}
        <div className="flex flex-wrap items-center justify-between gap-3 bg-card rounded-xl border border-border p-4 shadow-sm">
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" size="icon" onClick={() => setWeekOffset(o => o - 1)}><ChevronLeft className="h-4 w-4" /></Button>
            <Badge variant="outline" className="text-sm px-4 py-2" style={{ fontVariantNumeric: 'tabular-nums' }}>
              {weekMonday.toLocaleDateString(getLocale(), { day: 'numeric', month: 'long', year: 'numeric' })} — {t('planning.week')} {weekNumber}
            </Badge>
            <Button variant="outline" size="icon" onClick={() => setWeekOffset(o => o + 1)}><ChevronRight className="h-4 w-4" /></Button>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
              <Zap className={cn('h-4 w-4', oteOverlay ? 'text-amber-500' : 'text-muted-foreground')} />
              <span className="font-medium">{t('plan.oteOverlay')}</span>
              <Switch checked={oteOverlay} onCheckedChange={setOteOverlay} aria-label={t('plan.oteOverlayToggle')} />
            </label>
            <Button variant={weekOffset !== 0 ? 'default' : 'outline'} onClick={() => setWeekOffset(0)}>{t('planning.thisWeek')}</Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 sm:gap-6">
          {/* Main view */}
          <div className="lg:col-span-3 min-w-0">
            <Tabs defaultValue="week">
              <TabsList className="bg-card">
                <TabsTrigger value="week">{t('planning.weekView')}</TabsTrigger>
                <TabsTrigger value="gantt">{t('planning.ganttView')}</TabsTrigger>
              </TabsList>

              <TabsContent value="week" className="mt-4">
                <div className="overflow-x-auto -mx-1 px-1 pb-2">
                <div className="grid grid-cols-8 gap-3 sm:gap-4 min-w-[720px] w-full">
                  <div className="font-semibold w-20 shrink-0"></div>
                  {weekDays.map((day, idx) => (
                    <div
                      key={day}
                      className={cn(
                        'text-center text-xs sm:text-sm font-semibold py-2 px-1 rounded-lg min-w-[80px]',
                        idx === currentDayIdx && weekOffset === 0
                          ? 'bg-primary/10 text-primary font-bold'
                          : '',
                      )}
                    >
                      {day}
                      <div className={cn(
                        'text-[10px] font-normal',
                        idx === currentDayIdx && weekOffset === 0 ? 'text-primary/70' : 'text-muted-foreground',
                      )} style={{ fontVariantNumeric: 'tabular-nums' }}>
                        {weekDates[idx]}
                      </div>
                      {idx === currentDayIdx && weekOffset === 0 && (
                        <div className="mx-auto mt-0.5 w-1.5 h-1.5 rounded-full bg-primary" />
                      )}
                    </div>
                  ))}

                  {mergedPlanningData.lines.map((line) => (
                    <div key={line.id} className="contents">
                      <div className="flex items-center font-semibold text-sm shrink-0 pr-3">{t(line.name)}</div>
                      {line.schedule.map((slot, idx) => (
                        <div key={`${line.id}-${idx}`} className="min-w-[80px]">
                          <PlanningCell
                            slot={slot}
                            lineId={line.id}
                            materialColor={slot.material ? materialColors[slot.material] : null}
                            oteOverlay={oteOverlay}
                            onClick={() => {
                              if (slot.material) {
                                setDialogSlot(slot);
                                setDialogLine(t(line.name));
                                setDialogLineId(line.id);
                              } else {
                                setAddDialogLine(line);
                                setAddDialogDayIdx(idx);
                                setAddDialogOpen(true);
                              }
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
                </div>
              </TabsContent>

              <TabsContent value="gantt" className="mt-4">
                <GanttView
                  planningData={mergedPlanningData}
                  materialColors={materialColors}
                  onSlotClick={(slot, lineName, lineId) => {
                    setDialogSlot(slot);
                    setDialogLine(lineName);
                    setDialogLineId(lineId || '');
                  }}
                />
              </TabsContent>
            </Tabs>
          </div>

          {/* Sidebar */}
          <div className="space-y-4 min-w-0">
            {oteOverlay && (
              <>
                <SavingsCounter planningData={mergedPlanningData} />
                <OteChart />
                <EnergyPlanAdvisor />
              </>
            )}

            <Card className="">
              <CardHeader><CardTitle>{t('planning.capacity')}</CardTitle></CardHeader>
              <CardContent>
                <div className="relative">
                  <ResponsiveContainer width="100%" height={180}>
                    <PieChart>
                      <Pie data={utilizationData} cx="50%" cy="50%" innerRadius={50} outerRadius={75} paddingAngle={3} dataKey="value" stroke="none">
                        {utilizationData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center">
                      <div className="text-3xl font-bold" style={{ fontVariantNumeric: 'tabular-nums' }}>{utilizationData[0].value}%</div>
                      <div className="text-xs text-muted-foreground">{t('planning.scheduled')}</div>
                    </div>
                  </div>
                </div>
                <div className="mt-3 space-y-2">
                  {utilizationData.map(item => (
                    <div key={item.name} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                        <span>{item.name}</span>
                      </div>
                      <span className="font-semibold" style={{ fontVariantNumeric: 'tabular-nums' }}>{item.value}%</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Material legend */}
            <Card className="">
              <CardHeader><CardTitle className="text-sm">{t('planning.materials')}</CardTitle></CardHeader>
              <CardContent className="space-y-2.5">
                {Object.entries(materialColors).map(([mat, color]) => {
                  const label = mat === 'Limestone GCC' ? t('planning.material.gcc')
                    : mat === 'Limestone PCC' ? t('planning.material.pcc')
                    : mat === 'By-product' ? t('planning.material.byproduct')
                    : mat;
                  return (
                    <div key={mat} className="flex items-center gap-2.5 text-sm">
                      <div className="w-5 h-3.5 rounded" style={{ backgroundColor: color }} />
                      <span>{label}</span>
                    </div>
                  );
                })}
                <p className="text-xs text-muted-foreground pt-2 border-t border-border/60 mt-2">
                  <span className="font-medium text-foreground">{t('planning.productionFlow')}:</span> {t('planning.productionFlowDesc')}
                </p>
              </CardContent>
            </Card>

            {/* Weekly stats */}
            <Card className="">
              <CardHeader><CardTitle className="text-sm">{t('planning.weeklySummary')}</CardTitle></CardHeader>
              <CardContent className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('planning.plannedOutput')}</span>
                  <span className="font-semibold" style={{ fontVariantNumeric: 'tabular-nums' }}>{totalPlanned.toLocaleString()}t</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('planning.actualOutput')}</span>
                  <span className="font-semibold" style={{ fontVariantNumeric: 'tabular-nums' }}>{totalActual.toLocaleString()}t</span>
                </div>
                {totalPlanned > 0 && (
                  <div className="space-y-1">
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>{t('planning.completion')}</span>
                      <span className="font-semibold text-foreground" style={{ fontVariantNumeric: 'tabular-nums' }}>{Math.round((totalActual / totalPlanned) * 100)}%</span>
                    </div>
                    <Progress value={Math.round((totalActual / totalPlanned) * 100)} className="h-2" />
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('planning.avgUtilization')}</span>
                  <span className="font-semibold" style={{ fontVariantNumeric: 'tabular-nums' }}>{avgUtilization}%</span>
                </div>
              </CardContent>
            </Card>

            {/* Energy tariffs */}
            <Card className="">
              <CardHeader>
                <CardTitle className="text-sm">{t('planning.energyTariffs')}</CardTitle>
                <p className="text-[10px] text-muted-foreground mt-1">{t('plan.oteSpotNote')}</p>
              </CardHeader>
              <CardContent className="space-y-2.5">
                {planningData.energyTariffs.map((tariff, i) => {
                  const style = tariff.type === 'negative' ? 'bg-blue-100 text-blue-700 border-blue-200'
                    : tariff.type === 'offpeak' ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
                    : tariff.type === 'standard' ? 'bg-amber-100 text-amber-700 border-amber-200'
                    : 'bg-rose-100 text-rose-700 border-rose-200';
                  const label = tariff.type === 'negative' ? t('planning.tariffNegative')
                    : tariff.type === 'offpeak' ? t('planning.off')
                    : tariff.type === 'standard' ? t('planning.tariffStandard')
                    : t('planning.peak');
                  return (
                    <div key={i} className="flex justify-between text-sm">
                      <span style={{ fontVariantNumeric: 'tabular-nums' }}>{tariff.period}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-muted-foreground" style={{ fontVariantNumeric: 'tabular-nums' }}>
                          {tariff.rate.toLocaleString(getLocale())} {tariff.unit}
                        </span>
                        <Badge className={cn('text-[10px] border', style)}>{label}</Badge>
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Production Order Dialog */}
      <ProductionOrderDialog
        open={!!dialogSlot}
        onOpenChange={(open) => !open && setDialogSlot(null)}
        slot={dialogSlot}
        lineName={dialogLine}
        lineId={dialogLineId}
        onNavigateToOrder={(orderId) => {
          setDialogSlot(null);
          navigate(`/objednavky?highlight=${orderId}`);
        }}
      />

      {/* Add Planning Dialog */}
      <AddPlanningDialog
        open={addDialogOpen}
        onOpenChange={setAddDialogOpen}
        lineName={addDialogLine?.name}
        dayName={addDialogLine ? weekDays[addDialogDayIdx] : ''}
        orders={orders}
        materialColors={materialColors}
        onAdd={(newSlot) => {
          if (addDialogLine != null && addDialogDayIdx != null) {
            const key = `${addDialogLine.id}-${addDialogDayIdx}`;
            setSlotOverrides(prev => ({ ...prev, [key]: newSlot }));
          }
          setAddDialogLine(null);
          setAddDialogDayIdx(null);
        }}
      />
    </PageLayout>
  );
}
